import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({
  title,
  description,
  keywords,
  image,
  type = 'website',
  author = 'Meller Racing',
  noindex = false,
}) => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.meller-racing.com';
  const currentUrl = `${siteUrl}${location.pathname}`;

  const tWithFallback = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  
  // Helper function to check if a value is an unresolved translation key
  const isUnresolvedTranslation = (value) => {
    if (typeof value !== 'string') return false;
    // Check if it's a translation key (contains dots like seo.home.title)
    // or if it exactly matches the key pattern
    return value.includes('seo.') || value === 'seo.home.title' || value === 'seo.tuning.title' || 
           value === 'seo.products.title' || value === 'seo.shop.title' || value === 'seo.contact.title';
  };

  const defaultTitle = tWithFallback('seo.defaults.title', '🏍️ Meller Racing - Professional Motorcycle Tuning & Performance Parts');
  const defaultDescription = tWithFallback('seo.defaults.description', 'Professional motorcycle tuning services and high-performance parts. Handheld tuners, exhaust systems, and racing accessories.');
  const defaultImage = `${siteUrl}/logo-dark.png`;
  const defaultKeywords = tWithFallback('seo.defaults.keywords', 'motorcycle tuning, bike performance, ECU tuning, motorcycle parts, Meller Racing, Akrapovic, exhaust systems');

  const localeMap = {
    en: 'en_US',
    pl: 'pl_PL',
    de: 'de_DE',
    cs: 'cs_CZ',
  };
  const activeLang = (language || 'en').split('-')[0].toLowerCase();
  const ogLocale = localeMap[activeLang] || 'en_US';
  const alternateLocales = Object.values(localeMap).filter((locale) => locale !== ogLocale);

  // Validate that title/description/keywords are actual translations, not keys
  const finalTitle = title && !isUnresolvedTranslation(title) ? title : null;
  const finalDescription = description && !isUnresolvedTranslation(description) ? description : null;
  const finalKeywords = keywords && !isUnresolvedTranslation(keywords) ? keywords : null;

  const seoTitle = finalTitle ? `${finalTitle} | Meller Racing` : defaultTitle;
  const seoDescription = finalDescription || defaultDescription;
  const seoImage = image || defaultImage;
  const seoKeywords = finalKeywords || defaultKeywords;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={currentUrl} />
      
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:site_name" content="Meller Racing" />
      <meta property="og:locale" content={ogLocale} />
      {alternateLocales.map((locale) => (
        <meta key={locale} property="og:locale:alternate" content={locale} />
      ))}

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={seoTitle} />
      <meta property="twitter:description" content={seoDescription} />
      <meta property="twitter:image" content={seoImage} />

      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content={activeLang} />
      <meta name="revisit-after" content="7 days" />
    </Helmet>
  );
};

export default SEO;
